/**
 * Task-to-route mapping for the task-based navigation system.
 * Each task slug maps to the set of route prefixes it unlocks.
 */
export const TASK_ROUTE_MAP: Record<string, string[]> = {
  "front-office": [
    "/accessioning",
    "/dashboard",
    "/work-lists",
    "/send-outs",
    "/corrections-queue",
  ],
  grossing: ["/grossing"],
  embedding: ["/embedding"],
  cutting: ["/cutting", "/staining"],
  distribution: ["/distribution", "/send-outs", "/shipping"],
  "pathologist-review": [
    "/pathologist-review",
    "/discussion-queue",
    "/qa-review",
    "/qa-metrics",
    "/stain-metrics",
    "/report-delivery",
    "/distribution/control-slides",
    "/send-outs",
    "/corrections-queue",
  ],
  admin: ["/admin", "/report-delivery"],
};

/**
 * The default landing route for each task slug (in priority order).
 * The first matching task in the user's selected tasks wins.
 */
export const TASK_DEFAULT_ROUTE: Record<string, string> = {
  "front-office": "/dashboard",
  grossing: "/grossing",
  embedding: "/embedding",
  cutting: "/cutting",
  distribution: "/distribution",
  "pathologist-review": "/pathologist-review",
  admin: "/admin",
};

/**
 * Returns the best landing route given the selected task slugs.
 * Falls back to /cases if no task has a known default.
 */
export function getDefaultRoute(selectedTaskSlugs: string[]): string {
  const match = selectedTaskSlugs.find((slug) => !!TASK_DEFAULT_ROUTE[slug]);
  return match ? TASK_DEFAULT_ROUTE[match] : "/cases";
}

/**
 * Routes that are always visible regardless of task selection.
 */
export const ALWAYS_VISIBLE_ROUTES = [
  "/pending-work",
  "/cases",
  "/errors",
  "/billing",
  "/account",
];

/**
 * Returns true if a route is allowed given the set of selected task slugs.
 * Always-visible routes always pass.
 */
export function isRouteAllowed(
  path: string,
  selectedTaskSlugs: string[]
): boolean {
  const matchesRoute = (route: string) =>
    path === route || path.startsWith(`${route}/`);

  if (ALWAYS_VISIBLE_ROUTES.some(matchesRoute)) return true;

  return selectedTaskSlugs.some((slug) =>
    (TASK_ROUTE_MAP[slug] || []).some(matchesRoute)
  );
}
